/**
 * SignaturePad — draw or type. Returns PNG data URL via onChange when drawn,
 * or echoes typed string via onTypedChange. Self-contained canvas (no npm dep).
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { RotateCcw, PenLine, Keyboard } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';

export default function SignaturePad({ onDrawnChange, onTypedChange, typedValue = '' }) {
    const canvasRef = useRef(null);
    const wrapperRef = useRef(null);
    const [mode, setMode] = useState('draw'); // 'draw' | 'type'
    const [isEmpty, setIsEmpty] = useState(true);
    const drawing = useRef(false);
    const last = useRef(null);

    const init = useCallback(() => {
        const canvas = canvasRef.current;
        const wrap = wrapperRef.current;
        if (!canvas || !wrap) return;
        const rect = wrap.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;
        const ctx = canvas.getContext('2d');
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        ctx.strokeStyle = '#0B3C5D';
        ctx.lineWidth = 2.2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
    }, []);

    useEffect(() => {
        init();
        const ro = new ResizeObserver(init);
        if (wrapperRef.current) ro.observe(wrapperRef.current);
        return () => ro.disconnect();
    }, [init, mode]);

    const getPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const touch = e.touches?.[0] || e;
        return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
    };

    const start = (e) => {
        e.preventDefault();
        drawing.current = true;
        last.current = getPos(e);
    };
    const move = (e) => {
        if (!drawing.current) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const p = getPos(e);
        ctx.beginPath();
        ctx.moveTo(last.current.x, last.current.y);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        last.current = p;
        setIsEmpty(false);
    };
    const end = () => {
        if (!drawing.current) return;
        drawing.current = false;
        const dataUrl = canvasRef.current.toDataURL('image/png');
        onDrawnChange?.(dataUrl);
    };

    const clear = () => {
        const canvas = canvasRef.current;
        canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
        setIsEmpty(true);
        onDrawnChange?.(null);
    };

    return (
        <div data-testid="signature-pad">
            <div className="flex items-center gap-2 mb-2">
                <button type="button" onClick={() => setMode('draw')} className={`h-8 px-3 rounded-md text-[12px] font-medium inline-flex items-center gap-1.5 ${mode === 'draw' ? 'bg-[color:var(--bm-primary)] text-white' : 'bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]'}`} data-testid="sig-mode-draw">
                    <PenLine size={13} /> Draw
                </button>
                <button type="button" onClick={() => setMode('type')} className={`h-8 px-3 rounded-md text-[12px] font-medium inline-flex items-center gap-1.5 ${mode === 'type' ? 'bg-[color:var(--bm-primary)] text-white' : 'bg-[color:var(--bm-bg-mute)] text-[color:var(--bm-text-muted)]'}`} data-testid="sig-mode-type">
                    <Keyboard size={13} /> Type
                </button>
                {mode === 'draw' && !isEmpty && (
                    <Button type="button" variant="outline" size="sm" onClick={clear} className="ml-auto h-8" data-testid="sig-clear-btn">
                        <RotateCcw size={13} className="mr-1" /> Clear
                    </Button>
                )}
            </div>
            {mode === 'draw' ? (
                <div
                    ref={wrapperRef}
                    className="rounded-md border border-[color:var(--bm-border)] bg-white"
                    style={{ height: 160, touchAction: 'none' }}
                >
                    <canvas
                        ref={canvasRef}
                        className="w-full h-full rounded-md cursor-crosshair"
                        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                        data-testid="sig-canvas"
                    />
                </div>
            ) : (
                <Input
                    value={typedValue}
                    onChange={(e) => onTypedChange?.(e.target.value)}
                    placeholder="Type your full name"
                    className="h-12 font-display text-[18px] italic"
                    style={{ fontFamily: "'Sora', cursive" }}
                    data-testid="sig-type-input"
                />
            )}
            <div className="text-[11px] text-[color:var(--bm-text-subtle)] mt-1.5">
                By signing you confirm authority to bind the company and accept the terms shown.
            </div>
        </div>
    );
}
